/**
 * Native push notifications (FCM via @capacitor-firebase/messaging).
 *
 * No-op on web (the PWA uses the service-worker web-push path instead). On
 * native it: requests permission, obtains the FCM token, registers the device
 * with the server (POST /api/mobile/device), and routes notification taps.
 *
 * The plugin (and firebase) are dynamically imported so they stay in a lazy
 * chunk that never loads in the browser bundle. Requires Firebase config files
 * (google-services.json / GoogleService-Info.plist) at native build time — see
 * docs/mobile-app.md Phase 0.
 */
import { Capacitor } from '@capacitor/core';
import { apiRequest } from '../services/api/core';
import { storageGet, storageSet } from './nativeStorage';
import { logger } from './logger';

const DEVICE_ID_KEY = 'buzzbit_device_id';

/** Stable per-install device id (persisted), used to upsert the MobileDevice row. */
function getOrCreateDeviceId(): string {
  let id = storageGet(DEVICE_ID_KEY);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    storageSet(DEVICE_ID_KEY, id);
  }
  return id;
}

async function registerDevice(
  token: string,
  platform: 'ios' | 'android',
  appVersion?: string,
): Promise<void> {
  try {
    await apiRequest('/mobile/device', {
      method: 'POST',
      body: JSON.stringify({ token, platform, deviceId: getOrCreateDeviceId(), appVersion }),
    });
  } catch (err) {
    logger.debug('[nativePush] device registration failed', err);
  }
}

let initialized = false;

/**
 * Initialise native push. Safe to call on web (no-op) and idempotent.
 * @param onOpenUrl called when the user taps a notification carrying a `url`.
 */
export async function initNativePush(onOpenUrl?: (url: string) => void): Promise<void> {
  if (initialized || !Capacitor.isNativePlatform()) return;
  initialized = true;

  try {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

    const perm = await FirebaseMessaging.requestPermissions();
    if (perm.receive !== 'granted') {
      initialized = false; // allow a later retry (e.g. user enables in settings)
      return;
    }

    const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';

    let appVersion: string | undefined;
    try {
      const { App } = await import('@capacitor/app');
      appVersion = (await App.getInfo()).version;
    } catch {
      /* version is optional */
    }

    const { token } = await FirebaseMessaging.getToken();
    if (token) await registerDevice(token, platform, appVersion);

    // Re-register when FCM rotates the token.
    await FirebaseMessaging.addListener('tokenReceived', (event) => {
      if (event?.token) void registerDevice(event.token, platform, appVersion);
    });

    // Notification tapped → deep-link into the app (e.g. the inbox/handoff).
    await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      const data = event?.notification?.data as Record<string, unknown> | undefined;
      const url = data?.url;
      if (typeof url === 'string' && onOpenUrl) onOpenUrl(url);
    });
  } catch (err) {
    logger.debug('[nativePush] init failed', err);
    initialized = false;
  }
}
